import { useCallback, useEffect, useState } from 'react';

// Shared backbone for body-class preferences (theme + density).
//
// Two consumers — useTheme + useDensity — both need the exact same
// shape: a localStorage-persisted value reflected as a body class
// from a fixed prefix, with FOUC prevention (apply on module load
// before React mounts) and cross-tab sync (storage event).
//
// Keeping the logic in one place means a fix or addition (e.g. system
// preference media-query listener) lands once, not twice.

/**
 * Reads the stored value, validates against the allowed set, falls
 * back if missing/invalid or if a resolver provides a smarter default
 * (e.g. prefers-color-scheme for theme on first visit).
 */
export function readPreference(key, valid, fallback, resolver) {
  if (typeof window === 'undefined') return fallback;
  const stored = window.localStorage?.getItem(key);
  if (valid.has(stored)) return stored;
  return resolver ? resolver() : fallback;
}

/** Applies `${prefix}${value}` to <body>, stripping any sibling class. */
export function applyBodyClass(prefix, valid, value) {
  if (typeof document === 'undefined') return;
  const body = document.body;
  valid.forEach((v) => body.classList.remove(`${prefix}${v}`));
  body.classList.add(`${prefix}${value}`);
}

/**
 * Persists a preference to localStorage AND keeps it in sync with the
 * `${prefix}${value}` body class. Used by useTheme / useDensity.
 *
 * @param {string}    key       localStorage key
 * @param {Set<string>} valid   allowed values (Set for O(1) `has`)
 * @param {string}    fallback  default when nothing stored / invalid
 * @param {string}    prefix    body class prefix, e.g. 'theme-'
 * @param {() => string} [resolver]  computes a smarter default on first
 *                                   visit (e.g. prefers-color-scheme)
 * @returns {[string, (next: string) => void]}
 */
export function usePreference(key, valid, fallback, prefix, resolver) {
  const [value, setValue] = useState(() =>
    readPreference(key, valid, fallback, resolver)
  );

  // Reflect + persist on every change. localStorage may throw in
  // private mode / when quota is exceeded; the body class still
  // applies for the session, so silent catch is fine.
  useEffect(() => {
    applyBodyClass(prefix, valid, value);
    try { window.localStorage?.setItem(key, value); } catch { /* ignore */ }
  }, [value, key, prefix, valid]);

  // Cross-tab sync — `storage` fires in OTHER tabs only. If the user
  // toggles theme in one tab, sibling tabs follow without reload.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e) => {
      if (e.key === key && valid.has(e.newValue)) setValue(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key, valid]);

  const update = useCallback((next) => {
    if (valid.has(next)) setValue(next);
  }, [valid]);

  return [value, update];
}
